/** @typedef {{ traceId: string, spanId: string, sessionId: string, parentSpanId?: string, operation?: string }} TraceContext */
/** @typedef {Record<string, unknown>} LogContext */
/** @typedef {Window & { __OPENCLAW_ANALYTICS__?: Array<Record<string, unknown>>, gtag?: (...args: unknown[]) => void }} AnalyticsWindow */

import { addErrorBreadcrumb, captureTrackedError } from './error-tracking.js';
import { incrementMetric } from './performance-metrics.js';
import { hasPrivacyConsent } from './privacy-compliance.js';

const SENSITIVE_KEY =
  /(pass(word)?|secret|token|api[-_]?key|credential|authorization|cookie|session|private[-_]?key|client[-_]?secret)/i;

/** @param {number} size */
function randomHex(size) {
  const alphabet = '0123456789abcdef';
  let value = '';
  for (let i = 0; i < size; i += 1) {
    value += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return value;
}

/** @type {TraceContext} */
const rootTraceContext = {
  traceId: randomHex(32),
  spanId: randomHex(16),
  sessionId: randomHex(16)
};

/** @param {unknown} value @param {number} [depth] @returns {unknown} */
function sanitize(value, depth = 0) {
  if (value === null || value === undefined) {
    return value;
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack
    };
  }

  if (typeof value !== 'object') {
    return value;
  }

  if (depth > 6) {
    return '[TRUNCATED]';
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitize(item, depth + 1));
  }

  /** @type {Record<string, unknown>} */
  const result = {};
  for (const [key, entry] of Object.entries(value)) {
    result[key] = SENSITIVE_KEY.test(key) ? '[REDACTED]' : sanitize(entry, depth + 1);
  }

  return result;
}

export function createLogger(scope = 'web') {
  /** @param {'info' | 'warn' | 'debug' | 'error'} level @param {string} message @param {LogContext | undefined} context @param {TraceContext | undefined} traceContext */
  function emit(level, message, context, traceContext) {
    const trace = traceContext ?? rootTraceContext;
    const sanitizedContext = context ? sanitize(context) : undefined;

    const payload = {
      timestamp: new Date().toISOString(),
      level,
      scope,
      message,
      trace: {
        traceId: trace.traceId,
        spanId: trace.spanId,
        parentSpanId: trace.parentSpanId,
        sessionId: trace.sessionId,
        operation: trace.operation
      },
      ...(sanitizedContext ? { context: sanitizedContext } : {})
    };

    addErrorBreadcrumb(level, message, {
      scope,
      traceId: trace.traceId,
      spanId: trace.spanId,
      ...(sanitizedContext ? { context: sanitizedContext } : {})
    });

    if (level === 'error') {
      const trackedError = context?.error instanceof Error ? context.error : new Error(message);
      captureTrackedError(
        trackedError,
        {
          scope,
          level,
          ...(sanitizedContext ? { context: sanitizedContext } : {})
        },
        trace
      );
    }

    incrementMetric(`web.log.${level}`, { scope });

    const writer =
      level === 'error' ? console.error : level === 'warn' ? console.warn : console.info;
    writer(JSON.stringify(payload));
  }

  return {
    /** @param {string} message @param {LogContext} [context] @param {TraceContext} [traceContext] */
    info(message, context, traceContext) {
      emit('info', message, context, traceContext);
    },
    /** @param {string} message @param {LogContext} [context] @param {TraceContext} [traceContext] */
    warn(message, context, traceContext) {
      emit('warn', message, context, traceContext);
    },
    /** @param {string} message @param {LogContext} [context] @param {TraceContext} [traceContext] */
    debug(message, context, traceContext) {
      emit('debug', message, context, traceContext);
    },
    /** @param {string} message @param {LogContext} [context] @param {TraceContext} [traceContext] */
    error(message, context, traceContext) {
      emit('error', message, context, traceContext);
    }
  };
}

/** @param {string} operation @param {TraceContext} [parent] @returns {TraceContext} */
export function createTraceContext(operation, parent = rootTraceContext) {
  return {
    operation,
    traceId: parent.traceId,
    spanId: randomHex(16),
    parentSpanId: parent.spanId,
    sessionId: parent.sessionId
  };
}

/** @param {string} eventName @param {Record<string, unknown>} [params] @param {TraceContext} [traceContext] */
export function trackProductEvent(eventName, params = {}, traceContext = createTraceContext(eventName)) {
  if (!hasPrivacyConsent('analytics')) {
    incrementMetric('web.analytics.dropped.no_consent', { event: eventName });
    return { eventName, traceContext, payload: null, dropped: 'analytics-consent-not-granted' };
  }

  const payload = {
    ...params,
    trace_id: traceContext.traceId,
    span_id: traceContext.spanId,
    parent_span_id: traceContext.parentSpanId,
    session_id: traceContext.sessionId
  };

  if (typeof window !== 'undefined') {
    const typedWindow = /** @type {AnalyticsWindow} */ (window);

    if (!Array.isArray(typedWindow.__OPENCLAW_ANALYTICS__)) {
      typedWindow.__OPENCLAW_ANALYTICS__ = [];
    }

    typedWindow.__OPENCLAW_ANALYTICS__.push({
      event: eventName,
      timestamp: new Date().toISOString(),
      ...payload
    });

    if (typeof typedWindow.gtag === 'function') {
      typedWindow.gtag('event', eventName, payload);
    }
  }

  incrementMetric('web.analytics.sent', { event: eventName });

  return { eventName, traceContext, payload };
}
